import axios, { AxiosInstance, AxiosResponse } from 'axios';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL;

export interface ApiError {
  message: string;
  status: number;
  code?: string;
  details?: unknown;
}

class ApiClient {
  private instance: AxiosInstance;

  constructor() {
    this.instance = axios.create({
      baseURL: API_BASE_URL,
      withCredentials: true, // Important for WorkOS session cookies
      headers: {
        'Content-Type': 'application/json',
      },
    });

    // Response interceptor to handle API response format
    this.instance.interceptors.response.use(
      (response: AxiosResponse) => {
        // Extract data from the standard API envelope
        if (response.data && typeof response.data === 'object' && 'data' in response.data) {
          return { ...response, data: response.data.data };
        }
        return response;
      },
      (error) => {
        // Handle API errors - preserve error code and details for specific error handling
        const apiError: ApiError = {
          message: error.response?.data?.error?.message || error.message || 'An error occurred',
          status: error.response?.status || 500,
          code: error.response?.data?.error?.code,
          details: error.response?.data?.error?.details,
        };
        return Promise.reject(apiError);
      },
    );
  }

  async get<T>(url: string): Promise<T> {
    const response = await this.instance.get<T>(url);
    return response.data;
  }

  async post<T>(url: string, data?: unknown): Promise<T> {
    const response = await this.instance.post<T>(url, data);
    return response.data;
  }

  async put<T>(url: string, data?: unknown): Promise<T> {
    const response = await this.instance.put<T>(url, data);
    return response.data;
  }

  async patch<T>(url: string, data?: unknown): Promise<T> {
    const response = await this.instance.patch<T>(url, data);
    return response.data;
  }

  async delete<T>(url: string): Promise<T> {
    const response = await this.instance.delete<T>(url);
    return response.data;
  }
}

export const api = new ApiClient();
